import { useEffect, useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import {
  getBondCatalog,
  PROGRAM_PREAPPROVAL_SUBPRODUCT_KEY,
} from "@/lib/brokerBonds";

// BRK-S160 — which subproduct runs the STANDALONE pre-approval.
//
// The wizard branches on the resolved configuration's `flow` and on nothing
// product-specific. This hook is the single exception, and it is not a branch:
// it answers "which subproduct do I link to?" for the account's **Get
// pre-approved** button, which is a question about the CATALOG rather than
// about the flow. Keeping the `key` lookup here — and nowhere near
// `NewBondSubmissionPage` — is what stops the portal growing a second idea of
// what makes a flow standalone.
//
// A FAILED read, a catalog with no such subproduct, and a PMS that has not been
// given the row yet are all one fact to a caller: `null`, and no CTA. Nothing
// here may throw or block a page — the account profile is readable regardless,
// and a missing button is a worse day than a broken one but not a broken page
// (NN #7).
//
// Deliberately NOT cached across mounts. The catalog is a small read the picker
// already makes on every visit to the wizard, and a module-level cache would be
// client state PMS is the record for (NN #9).

/** The standalone pre-approval subproduct's id, or `null` when the catalog
 *  carries none (and while the read is still in flight). */
export function usePreapprovalSubproductId(): string | null {
  const { authedRequest } = useAuth();
  const [id, setId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const page = await getBondCatalog(authedRequest);
        if (cancelled) return;
        const found = page.items
          .flatMap((product) => product.subproducts)
          .find((sub) => sub.key === PROGRAM_PREAPPROVAL_SUBPRODUCT_KEY);
        setId(found?.id ?? null);
      } catch {
        // Silent on purpose: the CTA is an offer, not a state the broker is
        // waiting on, and an error banner over a program block would report a
        // failure about something else entirely.
        if (!cancelled) setId(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedRequest]);

  return id;
}
